import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import './Courses.css';

const FALLBACK_IMG = '/assets/hero/hero-bg.webp';

function ResourceRow({ resource }) {
  if (resource.kind === 'link') {
    return (
      <a href={resource.url} target="_blank" rel="noopener noreferrer" className="resource-row resource-row--link">
        <span className="resource-row__kind">Link</span>
        <span className="resource-row__title">{resource.title}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 3h7v7M10 14L21 3M21 14v7H3V3h7" />
        </svg>
      </a>
    );
  }
  if (resource.kind === 'text') {
    return (
      <div className="resource-row resource-row--text">
        <span className="resource-row__kind">Notes</span>
        <div>
          <div className="resource-row__title">{resource.title}</div>
          <div className="resource-row__body" dangerouslySetInnerHTML={{ __html: resource.body_text || '' }} />
        </div>
      </div>
    );
  }
  if (resource.kind === 'file') {
    return (
      <a href={resource.download_url} className="resource-row resource-row--file">
        <span className="resource-row__kind">{resource.is_gated ? '🔒 File' : 'File'}</span>
        <span className="resource-row__title">{resource.title}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
        </svg>
      </a>
    );
  }
  return null;
}

function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState('');

  const refresh = () => {
    return api.getCourseBySlug(slug).then((data) => setCourse(data));
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.getCourseBySlug(slug)
      .then((data) => {
        if (!active) return;
        setCourse(data);
        document.title = `${data.title} · Mind Spa`;
      })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  if (loading) return <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>Loading course…</div>;
  if (notFound || !course) {
    return (
      <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>
        <h2>Course not found</h2>
        <p style={{ marginTop: 12 }}><Link to="/courses" style={{ color: 'var(--accent)' }}>← Back to all courses</Link></p>
      </div>
    );
  }

  const isOpen = course.access_mode === 'open';
  const isEnquire = course.pricing_mode === 'enquire';
  const isFree = course.pricing_mode === 'free';
  const isPaid = course.pricing_mode === 'paid';
  const price = Number(course.cost_inr) || 0;
  const enrollmentStatus = course.enrollment_status; // null | pending | paid | self_enrolled | cancelled
  const hasAccess = course.can_see_resources;

  const handleSignIn = () => {
    navigate(`/student/login?return_to=${encodeURIComponent(`/courses/${slug}`)}`);
  };

  const handleSelfEnrol = async () => {
    if (!course.is_signed_in) { handleSignIn(); return; }
    setActionError('');
    setActionPending(true);
    try {
      await api.selfEnrolCourse(course.id);
      await refresh();
    } catch (err) {
      setActionError(err.message || 'Could not enrol. Please try again.');
    } finally {
      setActionPending(false);
    }
  };

  const handlePayClick = async () => {
    if (!course.is_signed_in) { handleSignIn(); return; }
    setActionError('');
    setActionPending(true);
    try {
      await api.startCoursePayment(course.id);
      await refresh();
    } catch (err) {
      setActionError(err.message || 'Could not start enrollment.');
    } finally {
      setActionPending(false);
    }
  };

  let ctaBlock = null;
  if (isOpen) {
    ctaBlock = (
      <div className="course-cta">
        <div className="course-cta__badge course-cta__badge--ok">✓ Open course — start reading below</div>
      </div>
    );
  } else if (enrollmentStatus === 'paid' || enrollmentStatus === 'self_enrolled') {
    ctaBlock = (
      <div className="course-cta">
        <div className="course-cta__badge course-cta__badge--ok">
          ✓ You're enrolled{enrollmentStatus === 'self_enrolled' ? ' (free)' : ''}.
        </div>
      </div>
    );
  } else if (enrollmentStatus === 'pending') {
    ctaBlock = (
      <div className="course-cta">
        <div className="course-cta__badge course-cta__badge--pending">
          ⏳ Payment under review — we'll confirm by email shortly.
        </div>
      </div>
    );
  } else if (isEnquire) {
    ctaBlock = <CourseEnquireBlock course={course} signedIn={course.is_signed_in} />;
  } else if (isFree) {
    ctaBlock = (
      <div className="course-cta">
        <button type="button" className="btn-primary course-cta__btn" onClick={handleSelfEnrol} disabled={actionPending}>
          {actionPending ? 'Enrolling…' : 'Enrol for free'}
        </button>
        {!course.is_signed_in && <p className="course-cta__hint">You'll be asked to sign in first.</p>}
      </div>
    );
  } else if (isPaid) {
    ctaBlock = (
      <div className="course-cta">
        <div className="course-cta__price">₹{price.toLocaleString('en-IN')}</div>
        <button type="button" className="btn-primary course-cta__btn" onClick={handlePayClick} disabled={actionPending}>
          {actionPending ? 'Starting…' : 'Enrol & pay'}
        </button>
        {!course.is_signed_in && <p className="course-cta__hint">You'll be asked to sign in first.</p>}
      </div>
    );
  }

  return (
    <div className="course-detail">
      <section className="course-hero">
        <div className="course-hero__bg"><img src={course.image || FALLBACK_IMG} alt="" /></div>
        <div className="course-hero__overlay" />
        <div className="container course-hero__inner">
          <div className="course-hero__crumbs">
            <Link to="/">Home</Link> &nbsp;/&nbsp; <Link to="/courses">Courses</Link> &nbsp;/&nbsp; <span>{course.title}</span>
          </div>
          <h1 className="course-hero__title">{course.title}</h1>
          {course.subtitle && <p className="course-hero__subtitle">{course.subtitle}</p>}
        </div>
      </section>

      <div className="container">
        <div className="course-detail__grid">
          <div>
            <div className="course-card-panel">
              <h2 className="course-card-panel__title">About this course</h2>
              <div className="course-card-panel__body" dangerouslySetInnerHTML={{ __html: course.description || '' }} />
            </div>

            {course.instructors && course.instructors.length > 0 && (
              <div className="course-card-panel">
                <h2 className="course-card-panel__title">
                  {course.instructors.length === 1 ? 'Instructor' : 'Instructors'}
                </h2>
                <div className="course-instructors">
                  {course.instructors.map((i) => (
                    <div key={i.id} className="course-instructor">
                      <div className="course-instructor__avatar" style={{ background: i.gradient || 'var(--bg-section)' }}>
                        {i.image
                          ? <img src={i.image} alt={i.name} loading="lazy" />
                          : <span>{i.initials}</span>}
                      </div>
                      <div className="course-instructor__meta">
                        <div className="course-instructor__name">{i.name}</div>
                        {i.title && <div className="course-instructor__title">{i.title}</div>}
                        {i.affiliation && <div className="course-instructor__aff">{i.affiliation}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="course-card-panel">
              <h2 className="course-card-panel__title">Chapters</h2>
              {course.chapters.length === 0 ? (
                <p style={{ color: 'var(--text-light)', margin: 0 }}>No chapters published yet.</p>
              ) : (
                <ol className="chapter-list">
                  {course.chapters.map((ch, idx) => (
                    <li key={ch.id} className="chapter-list__item">
                      <details open={hasAccess}>
                        <summary>
                          <span className="chapter-list__num">{String(idx + 1).padStart(2, '0')}</span>
                          <span className="chapter-list__title">{ch.title}</span>
                          {!hasAccess && <span className="chapter-list__lock">🔒</span>}
                        </summary>
                        {ch.description && (
                          <div className="chapter-list__desc" dangerouslySetInnerHTML={{ __html: ch.description }} />
                        )}
                        {hasAccess && ch.resources && ch.resources.length > 0 && (
                          <div className="chapter-list__resources">
                            {ch.resources.map((r) => <ResourceRow key={r.id} resource={r} />)}
                          </div>
                        )}
                        {!hasAccess && (
                          <div className="chapter-list__locked-msg">
                            Enrol to unlock the resources for this chapter.
                          </div>
                        )}
                      </details>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          <aside className="course-sidebar">
            <div className="course-card-panel">
              {actionError && <div className="course-cta__error">{actionError}</div>}
              {ctaBlock}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function CourseEnquireBlock({ course, signedIn }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim()) { setError('Name and email are required.'); return; }
    setSubmitting(true);
    try {
      await api.submitCourseEnquiry({ course_id: course.id, ...form });
      setSent(true);
    } catch (err) {
      setError(err.message || 'Could not send your enquiry.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="course-cta__badge course-cta__badge--ok">
        ✓ Enquiry sent — we'll be in touch.
      </div>
    );
  }
  return (
    <form className="course-enquire" onSubmit={handleSubmit}>
      <h3 className="course-enquire__title">Enquire about this course</h3>
      {error && <div className="course-cta__error">{error}</div>}
      <div className="field">
        <label htmlFor="ce-name">Name</label>
        <input id="ce-name" name="name" value={form.name} onChange={handleChange} required maxLength={120} />
      </div>
      <div className="field">
        <label htmlFor="ce-email">Email</label>
        <input id="ce-email" name="email" type="email" value={form.email} onChange={handleChange} required maxLength={190} />
      </div>
      <div className="field">
        <label htmlFor="ce-phone">Phone (optional)</label>
        <input id="ce-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} maxLength={40} />
      </div>
      <div className="field">
        <label htmlFor="ce-message">Message (optional)</label>
        <textarea id="ce-message" name="message" value={form.message} onChange={handleChange} maxLength={4000}
                  placeholder="Pricing, dates, group bookings…" />
      </div>
      <button type="submit" className="btn-primary course-cta__btn" disabled={submitting}>
        {submitting ? 'Sending…' : 'Send enquiry'}
      </button>
    </form>
  );
}

export default CourseDetail;
